import React, { useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';

import downloadHeatmapPdf from 'helper/Meteorology/TrackingForecast/DetailedView/heatmapPdf';
import heatmapMatrixFromOption from 'helper/Meteorology/TrackingForecast/DetailedView/heatmapMatrixFromOption';
import useDownloadDivImage from 'hooks/useDownloadDivImage';

/**
 * Download da Visão Detalhada do Acompanhamento de Previsão (Agro e Campos).
 *
 * Com o heatmap na tela, baixa a tabela INTEIRA em PDF (#2009, item 8): a imagem
 * fotografava só a janela visível (~13 cidades). No gráfico de linhas, segue a imagem.
 * O nome é passado sem extensão; ela vem do formato.
 */
export default function useHeatmapAwareDownload(
  imageRef: React.RefObject<HTMLDivElement | null>,
  option: object | undefined,
) {
  const downloadImage = useDownloadDivImage(imageRef);
  const matrix = useMemo(() => heatmapMatrixFromOption(option), [option]);

  const download = useCallback((basename: string) => () => {
    if (matrix) {
      // Se o PDF falhar (ex.: o chunk do jsPDF não carregou depois de um deploy), o
      // usuário ainda leva a imagem, e o Sentry fica sabendo.
      (async () => {
        try {
          await downloadHeatmapPdf(matrix, `${basename}.pdf`);
        } catch (error) {
          Sentry.captureException(error, {
            tags: { feature: 'tracking-forecast-heatmap-pdf' },
            extra: { basename, rows: matrix.rows.length, dates: matrix.dates.length },
          });
          downloadImage(`${basename}.png`)();
        }
      })();
      return;
    }
    downloadImage(`${basename}.png`)();
  }, [matrix, downloadImage]);

  return { download, label: matrix ? 'Baixar PDF' : 'Baixar imagem' };
}
